import React, { useState } from 'react';
import { X, AlertTriangle, Trash2, Shield, Clock } from 'lucide-react';
import { FileMetadata, FolderStructure } from '../../../shared/types';
import { formatFileSize, formatDate } from '../utils/formatters';

interface AdminDeletionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  selectedFiles: (FileMetadata & { ownerEmail: string })[];
  selectedFolders: (FolderStructure & { ownerEmail: string })[];
  isDeleting: boolean;
  adminEmail: string;
}

export const AdminDeletionConfirmationModal: React.FC<AdminDeletionConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedFiles,
  selectedFolders,
  isDeleting,
  adminEmail
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  const totalItems = selectedFiles.length + selectedFolders.length;
  const requiredConfirmationText = 'DELETE';
  const canProceed = confirmationText === requiredConfirmationText;

  // Get unique owners
  const uniqueOwners = new Set([
    ...selectedFiles.map(f => f.ownerEmail),
    ...selectedFolders.map(f => f.ownerEmail)
  ]);

  const handleConfirm = async () => {
    if (canProceed) {
      await onConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Admin Deletion Confirmation
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="mt-4">
          {/* Admin Warning */}
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-red-800">
                  Storage Administrator Action
                </h4>
                <div className="mt-2 text-sm text-red-700">
                  <p className="mb-2">
                    <strong>Warning:</strong> You are about to perform an administrative deletion that will:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Delete files/folders belonging to other users</li>
                    <li>Bypass normal deletion eligibility requirements</li>
                    <li>Be permanently logged with your administrator identity</li>
                    <li>Cannot be undone</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Deletion Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Items to delete:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
                  {selectedFiles.length > 0 && selectedFolders.length > 0 ? ', ' : ''}
                  {selectedFolders.length > 0 && (
                    <>{selectedFolders.length} folder{selectedFolders.length !== 1 ? 's' : ''}</>
                  )}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Affected users:</span>
                <span className="ml-2 font-medium text-gray-900">{uniqueOwners.size}</span>
              </div>
              <div>
                <span className="text-gray-600">Admin performing action:</span>
                <span className="ml-2 font-medium text-gray-900">{adminEmail}</span>
              </div>
              <div>
                <span className="text-gray-600">Timestamp:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {new Date().toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Archive Tier Notice */}
          {(selectedFiles.some(f => f.storageTier === 'archive') || 
            selectedFolders.some(f => f.folderPath.startsWith('/archive'))) && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start space-x-2">
                <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Archive Tier Override</p>
                  <p className="text-sm text-blue-800 mt-1">
                    As a Storage Administrator, you can delete Archive Tier items regardless of the 120-day eligibility requirement.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Details Toggle */}
          <div className="mb-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
          </div>

          {/* Detailed Item List */}
          {showDetails && (
            <div className="mb-4 max-h-64 overflow-y-auto border border-gray-200 rounded-md">
              <div className="divide-y divide-gray-200">
                {/* Files */}
                {selectedFiles.map((file, index) => (
                  <div key={`file-${index}`} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <Trash2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {file.fileName}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            file.storageTier === 'upload'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {file.storageTier === 'upload' ? 'Upload' : 'Archive'}
                          </span>
                        </div>
                        
                        <div className="mt-1 text-xs text-gray-500">
                          Owner: {file.ownerEmail} • 
                          {formatFileSize(file.sizeBytes)} • 
                          Uploaded {formatDate(file.uploadDate)}
                          {file.archivalDate && (
                            <> • Archived {formatDate(file.archivalDate)}</>
                          )}
                        </div>
                        
                        <div className="mt-1 text-xs text-gray-400">
                          Path: {file.folderPath}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Folders */}
                {selectedFolders.map((folder, index) => (
                  <div key={`folder-${index}`} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <Trash2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {folder.concertName}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            folder.folderPath.startsWith('/upload')
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {folder.folderPath.startsWith('/upload') ? 'Upload' : 'Archive'}
                          </span>
                        </div>
                        
                        <div className="mt-1 text-xs text-gray-500">
                          Owner: {folder.ownerEmail} • 
                          {folder.fileCount} files • 
                          {formatFileSize(folder.totalSizeBytes)} • 
                          Created {formatDate(folder.createdDate)}
                        </div>
                        
                        <div className="mt-1 text-xs text-gray-400">
                          Path: {folder.folderPath}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Confirmation Input */}
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-900 mb-2">
                  Type <code className="bg-yellow-100 px-1 rounded">DELETE</code> to confirm this administrative action:
                </p>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  disabled={isDeleting}
                  className="w-full px-3 py-2 border border-yellow-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canProceed || isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                Deleting...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2 inline-block" />
                Admin Delete {totalItems} Item{totalItems !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};